/**
 * Fill-in masks for Input: a pattern of fixed text and {TOKEN} blanks, e.g. "B{YY}{SEQ}".
 * Pure functions, no React, so they can be tested and reused.
 */
const TOKEN = /\{(\w+)\}/g;
/** The mask as fixed text and blanks, in order. */
export const parseMask = (mask, tokens = {}) => {
    const segments = [];
    let last = 0;
    mask.replace(TOKEN, (match, name, at) => {
        var _a;
        if (at > last)
            segments.push({ kind: 'literal', text: mask.slice(last, at) });
        const t = tokens[name] || {};
        segments.push({ kind: 'token', name, length: t.length, pattern: t.pattern, hint: (_a = t.hint) !== null && _a !== void 0 ? _a : name });
        last = at + match.length;
        return match;
    });
    if (last < mask.length)
        segments.push({ kind: 'literal', text: mask.slice(last) });
    return segments;
};
const fits = (seg, ch) => seg.kind === 'token' && (!seg.pattern || seg.pattern.test(ch));
/** Walks the value through the mask from the left. */
export const matchMask = (value, segments) => {
    let index = 0;
    let offset = 0;
    for (let i = 0; i < value.length; i++) {
        const ch = value[i];
        let placed = false;
        while (!placed && index < segments.length) {
            const seg = segments[index];
            if (seg.kind === 'literal') {
                if (seg.text[offset] !== ch)
                    return { matches: false, index, offset, full: false };
                offset++;
                placed = true;
                if (offset === seg.text.length) {
                    index++;
                    offset = 0;
                }
            }
            else if (seg.length) {
                if (!fits(seg, ch))
                    return { matches: false, index, offset, full: false };
                offset++;
                placed = true;
                if (offset === seg.length) {
                    index++;
                    offset = 0;
                }
            }
            else {
                // A free-length blank ends where the next fixed text starts.
                const next = segments[index + 1];
                if (offset > 0 && next && next.kind === 'literal' && next.text[0] === ch) {
                    index++;
                    offset = 0;
                    continue;
                }
                if (!fits(seg, ch))
                    return { matches: false, index, offset, full: false };
                offset++;
                placed = true;
            }
        }
        if (!placed)
            return { matches: false, index, offset, full: true };
    }
    const lastFree = index === segments.length - 1 && segments[index].kind === 'token' && !segments[index].length;
    return { matches: true, index, offset, full: index >= segments.length || (lastFree && offset > 0) };
};
/** The ghost after the value: what is left of the mask, with blanks shown by their hint. Empty when the value no
 * longer follows the mask. */
export const maskRest = (value, segments) => {
    const state = matchMask(value, segments);
    if (!state.matches)
        return [];
    const parts = [];
    segments.forEach((seg, i) => {
        if (i < state.index)
            return;
        const filled = i === state.index ? state.offset : 0;
        if (seg.kind === 'literal') {
            if (seg.text.length > filled)
                parts.push({ text: seg.text.slice(filled), blank: false });
        }
        else if (filled === 0) {
            parts.push({ text: seg.hint, blank: true });
        }
        else if (seg.length && seg.hint.length === seg.length && seg.hint.length > filled) {
            // A blank partly filled shows the rest of its hint when the hint has one character per place.
            parts.push({ text: seg.hint.slice(filled), blank: true });
        }
    });
    return parts;
};
/** Adds the fixed text that comes next, e.g. "B" at the start or "-" after a full blank. */
export const fillFixed = (value, segments) => {
    let out = value;
    for (let guard = 0; guard < segments.length; guard++) {
        const state = matchMask(out, segments);
        const seg = segments[state.index];
        if (!state.matches || !seg || seg.kind !== 'literal')
            break;
        out += seg.text.slice(state.offset);
    }
    return out;
};
/**
 * Strict mode: the longest start of the value that follows the mask. Fixed text left half deleted is removed whole,
 * so Backspace steps over it.
 */
export const strictValue = (value, previous, segments) => {
    let out = value;
    while (out && !matchMask(out, segments).matches)
        out = out.slice(0, -1);
    const state = matchMask(out, segments);
    if (state.full && out.length > previous.length && matchMask(previous, segments).full)
        return previous;
    if (out.length < previous.length) {
        const seg = segments[state.index];
        if (seg && seg.kind === 'literal' && state.offset > 0)
            out = out.slice(0, out.length - state.offset);
    }
    return out;
};
